import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { X, User, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { Input, Textarea, Button } from "../../components/ui";

const EditContactSidebar = ({ isOpen, onClose, contact, onContactUpdated }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    if (contact) {
      setFormData({
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        email: contact.email || "",
        phone: contact.phone || "",
        message: contact.message || "",
      });
    }
  }, [contact]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!contact?.id) return;
    try {
      setSaving(true);
      const { data } = await axiosInstance.put(`/contacts/update/${contact.id}`, formData);
      toast.success("Contact updated");
      onContactUpdated?.(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error updating contact");
    } finally {
      setSaving(false);
    }
  };

  const fullName = `${formData.firstName} ${formData.lastName}`.trim() || "Contact";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-pop"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <div>
                <h2 className="text-base font-semibold text-ink">Edit contact</h2>
                <p className="text-xs text-muted">{fullName}</p>
              </div>
              <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink" aria-label="Close">
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <form id="contact-form" onSubmit={handleSubmit} className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
              {/* quick actions */}
              {(formData.email || formData.phone) && (
                <div className="flex flex-wrap gap-2">
                  {formData.email && (
                    <a href={`mailto:${formData.email}`} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-xs font-medium text-muted transition hover:text-ink">
                      <EnvelopeSimple size={14} /> Email
                    </a>
                  )}
                  {formData.phone && (
                    <a href={`tel:${formData.phone}`} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-xs font-medium text-muted transition hover:text-ink">
                      <Phone size={14} /> Call
                    </a>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input label="First name" icon={User} name="firstName" value={formData.firstName} onChange={handleChange} />
                <Input label="Last name" icon={User} name="lastName" value={formData.lastName} onChange={handleChange} />
              </div>
              <Input label="Email" icon={EnvelopeSimple} type="email" name="email" value={formData.email} onChange={handleChange} />
              <Input label="Phone" icon={Phone} type="tel" name="phone" value={formData.phone} onChange={handleChange} />
              <Textarea label="Message" name="message" rows={5} value={formData.message} onChange={handleChange} />
            </form>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" form="contact-form" loading={saving}>Save changes</Button>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditContactSidebar;
